import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

const BILLING_CYCLES = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual',
};

const CYCLE_DAYS_BY_BILLING = {
  [BILLING_CYCLES.MONTHLY]: 30,
  [BILLING_CYCLES.ANNUAL]: 365,
};

export const useIsJussubClient = () => {
  const externalId = useSelector(
    (state) => state.subscription?.subscription_status?.external_id,
  );

  return Boolean(externalId);
};

export const mapJussubPlanItemsToBenefits = (planItems = []) =>
  (Array.isArray(planItems) ? planItems : []).map((item) => ({
    description: item.name,
    enabled: true,
  }));

export const useJussubPlanOffers = () => {
  const [billingCycle, setBillingCycle] = useState(BILLING_CYCLES.MONTHLY);

  const clientPlanOffers = useSelector((state) => state.app?.client_plan_offers);
  const isLoaded = useSelector(
    (state) => state.app?.is_client_plan_offers_loaded ?? false,
  );
  const isLoading = useSelector(
    (state) => state.app?.is_client_plan_offers_loading ?? false,
  );

  const cycleGroups = Array.isArray(clientPlanOffers) ? clientPlanOffers : [];

  const availableCycles = useMemo(
    () =>
      cycleGroups
        .map((group) => group?.cycleDays)
        .filter((cycleDays) => cycleDays != null),
    [cycleGroups],
  );

  const hasAnnualOffers = availableCycles.includes(365);
  const hasMonthlyOffers = availableCycles.includes(30);

  const activeCycleDays =
    CYCLE_DAYS_BY_BILLING[billingCycle] ??
    cycleGroups[0]?.cycleDays ??
    30;

  const activeOffers = useMemo(() => {
    const group = cycleGroups.find(
      (item) => Number(item?.cycleDays) === Number(activeCycleDays),
    );
    return Array.isArray(group?.offers) ? group.offers : [];
  }, [cycleGroups, activeCycleDays]);

  const plans = useMemo(() => {
    return activeOffers
      .map((offer) => offer?.plan)
      .filter(Boolean)
      .sort((a, b) => Number(b.amount) - Number(a.amount));
  }, [activeOffers]);

  const toggleBillingCycle = useCallback(() => {
    setBillingCycle((current) =>
      current === BILLING_CYCLES.MONTHLY
        ? BILLING_CYCLES.ANNUAL
        : BILLING_CYCLES.MONTHLY,
    );
  }, []);

  return {
    billingCycle,
    toggleBillingCycle,
    plans,
    isLoading: isLoading || !isLoaded,
    isLoaded,
    hasAnnualOffers,
    hasMonthlyOffers,
    showBillingToggle: hasAnnualOffers && hasMonthlyOffers,
  };
};
